import { formatCurrency, formatNumber } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, LOCALE_ID, OnDestroy, signal, ViewChild, OnInit, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { FileService, LoggingService, NavigationService, } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LayoutDirective, FlexDirective, LayoutAlignDirective } from "@ngbracket/ngx-layout/flex";
import { TruncateTextComponent, BusyIndicatorComponent } from "@tylertech/common-components-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";

@Component({
    selector: "inventory-item-orders-chart",
    templateUrl: "./itemOrdersChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class ItemOrdersChartComponent implements OnDestroy, OnInit
{
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);
    private readonly locale = inject(LOCALE_ID);

    @Input("chartId") private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;

    private _chartCriteria: IChartCriteria;
    isLoaded: boolean;
    chartConfig: ChartConfiguration<"bar">;

    readonly showChart = signal(false);

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    // get showChart(): boolean
    // {
    //     return !this.errorMessage && this.chartCriteria?.ChartDatas?.length > 0;
    // }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        const type = this.chartCriteria?.Parameters?.find(v => v.Id === "ItemOrdersChartType")?.Value;
        return type ? type + " Item Orders" : "Item Orders";
    }

    get ChartSubtitle(): string
    {
        const setting = this.chartCriteria.Parameters?.filter(v => v.Id === "ItemOrdersChartItemId")[0];
        if (!setting.Options)
        {
            return null;
        }
        const option = setting.Options?.filter(t => t.Value === setting.Value)[0];
        return !option ? null : option.Display;
    }

    private get chartType(): string
    {
        return this.chartCriteria?.Parameters?.find(v => v.Id === "ItemOrdersChartType")?.Value;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            (_resolveFn) =>
            {
                if (!this.chartCriteria?.ChartDatas?.length)
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!result?.ChartDatas?.length)
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors(2);
        const priorColor = chartColors[0];
        const currentColor = chartColors[1];
        const yearColor = priorColor;

        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: this.chartType === "Monthly",
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<"bar">) =>
                            {
                                const count = formatNumber(Number(tooltipItem.label), this.locale);
                                const currency = formatCurrency(this.chartCriteria.ChartDatas[0].Additional[0][tooltipItem.dataIndex], this.locale, "$");
                                return `Count: ${count} Amount: ${currency}`;
                            },
                        },
                    },
                },
                datasets: {
                    bar: {
                        yAxisID: "y",
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                    },
                },
                onClick: !this.chartCriteria.HideSettings
                    ? (_event: ChartEvent & Event, _elements: ActiveElement[], _chart: Chart): void =>
                    {
                        const id = this.chartCriteria?.Parameters?.filter(v => v.Id === "ItemOrdersChartItemId")[0].Value;
                        this.$navigationService.navigateToApp(`inventorySearch/itemManager/id/${id}`);
                    } : undefined,
            },
            data: this.chartType === "Monthly"
                ? {
                    datasets: [
                        {
                            type: "bar",
                            label: this.chartCriteria.ChartDatas[1].SetLabel,
                            data: this.chartCriteria.ChartDatas[1].Data[0],
                            backgroundColor: priorColor,
                            borderColor: priorColor,
                            hoverBackgroundColor: priorColor,
                            hoverBorderColor: priorColor,
                        },
                        {
                            type: "bar",
                            label: this.chartCriteria.ChartDatas[0].SetLabel,
                            data: this.chartCriteria.ChartDatas[0].Data[0],
                            backgroundColor: currentColor,
                            borderColor: currentColor,
                            hoverBackgroundColor: currentColor,
                            hoverBorderColor: currentColor,
                        }
                    ],
                    labels: this.chartCriteria.ChartDatas[0].Labels.map(x => x.substring(0, 3)),
                }
                : {
                    datasets: [
                        {
                            type: "bar",
                            data: this.chartCriteria.ChartDatas[0].Data[0],
                            backgroundColor: yearColor,
                            borderColor: yearColor,
                            hoverBackgroundColor: yearColor,
                            hoverBorderColor: yearColor,
                        }
                    ],
                    labels: this.chartCriteria.ChartDatas[0].Labels,
                },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
